import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@material-ui/core";
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
  DeleteOutlined,
  DownloadOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  NotificationsOutlined,
  ScheduleSendOutlined,
  Tune,
} from "@mui/icons-material";
import {
  addAllAvailableScheduleToCopyListAction,
  deleteMultipleScheduleReportAction,
  duplicateCopiedScheduleReport,
  getScheduleData,
  getUsersData,
  pushConfirmedScheduleSMSAction,
  pushRequestScheduleSMSAction,
  scheduleSetFilter,
  setDeleteModeAction,
  setManualModeAction,
} from "actions/scheduleActions";
import { openModal } from "actions/modalActions";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import { CustomDrawer } from "componentsNew/drawers/CustomDrawer";
import { ScheduleTableSettings } from "componentsNew/drawers/drawerContent/ScheduleTableSettings";
import { DateFilter } from "componentsNew/elements/DateFilter";
import { SearchInput } from "componentsNew/elements/SearchInput";
import { StationSelect } from "componentsNew/elements/SelectField";
import { ModalWindow } from "componentsNew/modals/ModalWindow";
import { SendNotificationModalContent } from "componentsNew/modals/modalContent/ScheduleModelContent";
import { SchedulePdfGenerationModal } from "componentsNew/modals/SchedulePdfGenerationModal";
import { SUPERADMIN } from "constants/constants";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { ScheduleFilters, ScheduleReport } from "types/Schedule";
import { UserData } from "types/User";
import { getTimeAfterPost } from "utils/filters";
import { getNextWeek } from "utils/helpers";
import { returnUserRole } from "utils/scheduleHelpers";
import { useStyles } from "./styles";

const getStatusParam = (statusList?: number[]) =>
  statusList?.length ? statusList.join("_") : "1_5";

const getUnpublishedPublishCount = (
  userList: UserData[],
  dataList: ScheduleReport[],
  idListForSMS: number[],
  currentUserRole: number,
) => {
  const includeSuperadmin = currentUserRole === SUPERADMIN;
  const visibleSchedules = dataList.filter(
    (item) => includeSuperadmin || returnUserRole(item.assigned_to, userList) !== SUPERADMIN,
  );
  if (!visibleSchedules.length) return "";

  const count = idListForSMS.length
    ? idListForSMS.length
    : userList.filter((user) => includeSuperadmin || user.role !== SUPERADMIN).filter((user) =>
        visibleSchedules.some(
          (schedule) =>
            schedule.assigned_to === user.id &&
            schedule.is_published === false &&
            schedule.role !== null,
        ),
      ).length;

  return ` (${count})`;
};

interface ActionItem {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

const ScheduleActionsMenu: FC<{
  idListForSMS: number[];
  userList: UserData[];
  dataList: ScheduleReport[];
  scheduleFilters: ScheduleFilters;
}> = ({ idListForSMS, userList, dataList, scheduleFilters }) => {
  const dispatch = useDispatch();
  const { userData } = useTypeSelectors((state) => state.auth);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const weekDate = useMemo(
    () => moment(scheduleFilters?.week_date, "GGGG-[W]WW"),
    [scheduleFilters?.week_date],
  );
  const unpublishedCount = useMemo(
    () => getUnpublishedPublishCount(userList, dataList, idListForSMS, userData.role),
    [userList, dataList, idListForSMS, userData.role],
  );

  const handleClose = () => setAnchorEl(null);
  const handleOpenModal = (modalName: string) => {
    handleClose();
    dispatch(openModal(modalName));
  };

  const actionItems: ActionItem[] = [
    {
      key: "send-availability",
      label: "Send Availability",
      icon: <ScheduleSendOutlined fontSize="small" />,
      onClick: () => handleOpenModal("availability-notify-modal"),
      disabled: weekDate.isSame(moment(), "week") || weekDate.isBefore(moment(), "week") || !idListForSMS.length,
    },
    {
      key: "add-publish-change",
      label: `Publish Changes${unpublishedCount}`,
      icon: <NotificationsOutlined fontSize="small" />,
      onClick: () => handleOpenModal("push-schedule-notify-modal"),
      disabled:
        weekDate.isBefore(moment(), "week") ||
        !dataList.length ||
        dataList.every((schedule) => getTimeAfterPost(schedule.schedule_date, 0)),
    },
    {
      key: "delete-schedules",
      label: "Delete Schedules",
      icon: <DeleteOutlined fontSize="small" />,
      onClick: () => {
        handleClose();
        dispatch(setDeleteModeAction());
      },
    },
    {
      key: "download-pdf",
      label: "Download PDF",
      icon: <DownloadOutlined fontSize="small" />,
      onClick: () => handleOpenModal("download-schedule-pdf-modal"),
    },
  ];

  return (
    <>
      <CustomButton
        buttonText={"Actions"}
        onClick={(event: any) => setAnchorEl(event?.currentTarget)}
        variant="contained"
        icon={anchorEl ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        swapPositions
      />
      <Menu
        id="schedule-actions-menu"
        anchorEl={anchorEl}
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {actionItems.map((item) => (
          <MenuItem key={item.key} onClick={item.onClick} disabled={item.disabled}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ScheduleHead: FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { scheduleFilters, idListForSMS, mode, dataList, userList } = useTypeSelectors(
    (state) => state.scheduleTable,
  );
  const { week_date, station, search, statusList, isTwoWeekTable } = scheduleFilters;
  const statuses = getStatusParam(statusList);

  useEffect(() => {
    const [year, week] = week_date.split("-W");
    const [nextYear, nextWeek] = isTwoWeekTable ? getNextWeek(week_date, 1)?.split("-W") ?? [] : [];
    dispatch(
      getScheduleData({
        week: Number(week) - 1,
        year_number: Number(year),
        statuses,
        ...(isTwoWeekTable && { next_week: Number(nextWeek) - 1, next_year: Number(nextYear) }),
      }),
    );
  }, [week_date, isTwoWeekTable, statuses, dispatch]);

  useEffect(() => {
    dispatch(getUsersData(search, Number(station), statuses));
  }, [search, station, statuses, dispatch]);

  const handleSearch = useCallback(
    (value: string) => {
      if ((search || "") === value) return;
      dispatch(scheduleSetFilter({ search: value }));
    },
    [dispatch, search],
  );

  if (mode !== "manual") {
    return (
      <Box className={classes.paper}>
        <Box className={classes.leftBox}>
          {mode === "delete" ? (
            <Typography>Deleting Shifts</Typography>
          ) : (
            <CustomButton
              buttonText={"Paste All Available"}
              theme={"blue"}
              onClick={() => dispatch(addAllAvailableScheduleToCopyListAction())}
              variant="contained"
            />
          )}
        </Box>
        <Box className={classes.leftBox}>
          <CustomButton
            buttonText={"Cancel"}
            onClick={() => dispatch(setManualModeAction())}
            variant="text"
          />
          <CustomButton
            buttonText={"Save"}
            theme={"blue"}
            onClick={() =>
              dispatch(
                mode === "delete"
                  ? deleteMultipleScheduleReportAction()
                  : duplicateCopiedScheduleReport(),
              )
            }
            variant="contained"
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.paper}>
      <Box className={classes.leftBox}>
        <StationSelect
          value={station ? Number(station) : 0}
          onChange={(value: string) => dispatch(scheduleSetFilter({ station: value }))}
        />
        <Box>
          <Typography className={classes.label}>Week</Typography>
          <DateFilter
            type={"week"}
            weekValue={week_date}
            weekChange={(value: string) => dispatch(scheduleSetFilter({ week_date: value }))}
            hasSwitch
          />
        </Box>
      </Box>
      <Box className={classes.rightBox}>
        <Box className={classes.leftBox}>
          <SearchInput value={search || ""} onChange={handleSearch} placeholder={"Name"} />
          <CustomDrawer
            customWidth={440}
            buttonType={"custom"}
            drawer_name={`filter-schedule-settings`}
            drawerTitle={`Table Settings`}
            customButtonElement={
              <CustomButton buttonText={"Settings"} onClick={() => null} variant="outlined" icon={<Tune />} />
            }
          >
            <ScheduleTableSettings />
          </CustomDrawer>
        </Box>
        <ScheduleActionsMenu
          idListForSMS={idListForSMS}
          userList={userList}
          dataList={dataList}
          scheduleFilters={scheduleFilters}
        />
        <ModalWindow
          buttonType={"none"}
          modal_name={"availability-notify-modal"}
          modalTitle={"Send availability request"}
          modalDescription={"SMS will be sent for all selected drivers for availability confirmation"}
        >
          <SendNotificationModalContent
            handleSubmitSMS={() =>
              dispatch(pushRequestScheduleSMSAction(idListForSMS.join("_"), `${week_date}`))
            }
          />
        </ModalWindow>
        <ModalWindow
          buttonType={"none"}
          modal_name={"push-schedule-notify-modal"}
          modalTitle={"Publish SMS"}
          modalDescription={`${idListForSMS.length ? "SMS will be sent to selected users" : "SMS will be sent to all users"}. It may take some time to deliver them.`}
        >
          <SendNotificationModalContent
            handleSubmitSMS={() =>
              dispatch(pushConfirmedScheduleSMSAction(station, `${week_date}`))
            }
          />
        </ModalWindow>
        <SchedulePdfGenerationModal />
      </Box>
    </Box>
  );
};

export default ScheduleHead;
